import React, { useEffect, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const handleLogout = () => {
    logout();
  };

  const toggleDropdown = (dropdownName: string) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
  };

  // Cerrar dropdowns cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".dropdown-container")) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-blue-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-blue-600">Fracto Admin</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-700">
                Bienvenido, {user?.nombres || "Usuario"}
              </span>
              <button
                onClick={handleLogout}
                className="text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors text-sm"
              >
                Cerrar Sesión
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white shadow-sm border-b border-blue-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => (window.location.href = "/admin")}
              className="py-4 px-2 border-b-2 border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-600 transition-colors"
            >
              Dashboard
            </button>

            {/* Dropdown Curricular */}
            <div className="relative dropdown-container">
              <button
                onClick={() => toggleDropdown("curricular")}
                className="py-4 px-2 border-b-2 border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-600 transition-colors flex items-center"
              >
                Curricular
                <svg
                  className={`ml-1 h-4 w-4 transition-transform ${
                    openDropdown === "curricular" ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {openDropdown === "curricular" && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                  <button
                    onClick={() =>
                      (window.location.href = "/admin/gestion-curricular")
                    }
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                  >
                    Gestión Curricular
                  </button>
                  <button
                    onClick={() =>
                      (window.location.href = "/admin/malla-curricular")
                    }
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                  >
                    Malla Curricular
                  </button>
                </div>
              )}
            </div>

            {/* Dropdown Modo Historia */}
            <div className="relative dropdown-container">
              <button
                onClick={() => toggleDropdown("historia")}
                className="py-4 px-2 border-b-2 border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-600 transition-colors flex items-center"
              >
                Modo Historia
                <svg
                  className={`ml-1 h-4 w-4 transition-transform ${
                    openDropdown === "historia" ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              {openDropdown === "historia" && (
                <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                  <button
                    onClick={() =>
                      (window.location.href = "/admin/gestion-modo-historia")
                    }
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                  >
                    Gestión Modo Historia
                  </button>
                  <button
                    onClick={() =>
                      (window.location.href = "/admin/modo-historia")
                    }
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-600"
                  >
                    Preguntas Oficiales
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => (window.location.href = "/admin/colegios")}
              className="py-4 px-2 border-b-2 border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-600 transition-colors"
            >
              Colegios
            </button>
            <button
              onClick={() => (window.location.href = "/admin/usuarios")}
              className="py-4 px-2 border-b-2 border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-600 transition-colors"
            >
              Usuarios
            </button>
            <button
              onClick={() => (window.location.href = "/admin/ejercicios-educativos")}
              className="py-4 px-2 border-b-2 border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-600 transition-colors"
            >
              Ejercicios Educativos
            </button>
            <button
              onClick={() => (window.location.href = "/admin/reportes")}
              className="py-4 px-2 border-b-2 border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-600 transition-colors"
            >
              Reportes
            </button>
            <button
              onClick={() => (window.location.href = "/admin/simulaciones")}
              className="py-4 px-2 border-b-2 border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-600 transition-colors"
            >
              Simulaciones
            </button>
            <button
              onClick={() => (window.location.href = "/admin/configuracion")}
              className="py-4 px-2 border-b-2 border-transparent text-gray-500 hover:text-blue-600 hover:border-blue-600 transition-colors"
            >
              Configuración
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};
